import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ideaService } from '../../services/api';
import { Idea } from '../../types/idea';

interface IdeasState {
    items: Idea[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: IdeasState = {
    items: [],
    status: 'idle',
    error: null,
};

export const fetchIdeas = createAsyncThunk(
    'ideas/fetchIdeas',
    async (filterParams: any = {}) => {
        const response = await ideaService.getAll(filterParams);
        // Sort ideas by id in descending order
        const sortedIdeas = response.data.sort((a: Idea, b: Idea) => b.id - a.id);
        return sortedIdeas;
    }
);

export const createIdea = createAsyncThunk(
    'ideas/createIdea',
    async (ideaData: any) => {
        const response = await ideaService.create(ideaData);
        return response.data;
    }
);

export const updateIdea = createAsyncThunk(
    'ideas/updateIdea',
    async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
        try {
            console.log('Starting updateIdea thunk for id:', id);
            const response = await ideaService.update(id, data);
            console.log('Update response in thunk:', response);
            if (!response) {
                throw new Error('No response received');
            }
            return response;
        } catch (error: any) {
            console.error('Error in updateIdea thunk:', error);
            return rejectWithValue(error.message || 'Failed to update idea');
        }
    }
);

export const deleteIdea = createAsyncThunk(
    'ideas/deleteIdea',
    async (id: number) => {
        await ideaService.delete(id);
        return id;
    }
);

const ideasSlice = createSlice({
    name: 'ideas',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchIdeas.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchIdeas.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload;
            })
            .addCase(fetchIdeas.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to fetch ideas';
            })
            .addCase(createIdea.fulfilled, (state, action) => {
                // Add new idea at the beginning of the array
                state.items.unshift(action.payload);
            })
            .addCase(updateIdea.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(updateIdea.fulfilled, (state, action) => {
                console.log('updateIdea.fulfilled - action payload:', action.payload);
                if (!action.payload) {
                    console.error('No payload received in updateIdea.fulfilled');
                    state.status = 'failed';
                    state.error = 'No data received from server';
                    return;
                }
                const index = state.items.findIndex(idea => idea.id === action.payload.id);
                console.log('Found index:', index);
                if (index !== -1) {
                    console.log('Updating idea at index:', index);
                    state.items[index] = action.payload;
                }
                state.status = 'succeeded';
                state.error = null;
                console.log('State after update:', state);
            })
            .addCase(updateIdea.rejected, (state, action) => {
                console.error('updateIdea.rejected:', action);
                state.status = 'failed';
                state.error = typeof action.payload === 'string' ? action.payload : 'Failed to update idea';
            })
            .addCase(deleteIdea.fulfilled, (state, action) => {
                state.items = state.items.filter(idea => idea.id !== action.payload);
            });
    },
});

export default ideasSlice.reducer; 